import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '../design-system';
import { useOrganization } from '../contexts/OrganizationContext';
import { useApplication, getLastRepositoryId } from '../contexts/ApplicationContext';

/**
 * Resolver for `/repositories`.
 *
 * The repositories list page has been replaced by the sidebar repository switcher, so this
 * route no longer renders a list — it redirects the user into a repository:
 *   - the org's last-viewed repository (if it still exists), otherwise the first repository
 *   - the onboarding connect flow when the org has no repositories yet
 */
const RepositoriesIndexPage: React.FC = () => {
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { applications, applicationsLoaded } = useApplication();

  // Wait until the repository list has actually been fetched for the current org,
  // otherwise we could briefly redirect to onboarding before the list arrives.
  if (orgLoading || !applicationsLoaded) {
    return (
      <div className="flex items-center justify-center w-full h-full py-20">
        <Spinner size={24} />
      </div>
    );
  }

  if (applications.length === 0) {
    return <Navigate to="/onboarding/connect" replace />;
  }

  const lastViewedId = getLastRepositoryId(currentOrganization?.id);
  const target =
    (lastViewedId && applications.some((app) => app.id === lastViewedId) && lastViewedId) ||
    applications[0].id;

  return <Navigate to={`/repositories/${target}`} replace />;
};

export default RepositoriesIndexPage;
